import React, { useState } from "react";
import { motion } from "framer-motion";
import { FileText, Download, ExternalLink, Newspaper, Mail, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const DOWNLOAD_RESOURCES = {
  "corporate-training-and-education-portfolio": {
    label: "Download Portfolio",
    fileName: "Corporate-Training-and-Education-Portfolio.pdf",
    description: "Please provide your name and email to gain access to the download.",
  },
  "oprisk-service-catalogue": {
    label: "Download Service Catalogue",
    fileName: "OpRisk-Service-Catalogue.pdf",
    description: "Please provide your name and email to gain access to the white paper.",
  },
  "risk-culture": {
    label: "Download Risk Culture White Paper",
    fileName: "DF-Whitepaper-Risk-Culture-Elena-Pykhova_V1.pdf",
    description: "Please provide your name and email to gain access to the white paper.",
  },
};

const RESOURCES = [
  {
    type: "Article",
    icon: Newspaper,
    title: "Why Operational Risk Must Sell Value, Not Process (p.46)",
    meta: "CeFPro Connect · 7 min read",
    url: "https://connect.cefpro.com/magazines/digital/view/september-edition-2026",
    action: "Read on CeFPro",
    external: true,
  },
  {
    type: "Free download",
    icon: Download,
    title: "Corporate Training and Education Portfolio",
    meta: "PDF · 7 pages",
    action: "Download PDF",
    external: false,
    resourceKey: "corporate-training-and-education-portfolio",
  },
  {
    type: "White paper",
    icon: FileText,
    title: "Operational Risk Service Catalogue",
    meta: "PDF · 17 pages",
    action: "Download PDF",
    external: false,
    resourceKey: "oprisk-service-catalogue",
  },
  {
    type: "White paper",
    icon: FileText,
    title: "Risk Culture",
    meta: "A Practical Guide for Risk Leaders <br/> Published in collaboration with Decision Focus.",
    action: "Download PDF",
    external: false,
    resourceKey: "risk-culture",
  },
  {
    type: "Email updates",
    icon: Mail,
    title: "Risk Insights & Updates",
    meta: "Regulatory developments, practical insights and forthcoming events.",
    action: "Sign up",
    external: false,
    resourceKey: "email-updates",
  },
  {
    type: "Professional community",
    icon: Users,
    title: "Best Practice Operational Risk Forum",
    meta: "A practitioner-led community for sharing experience and best practice.",
    action: "Express interest",
    external: false,
    resourceKey: "professional-community",
  },
];

const submitNetlifyForm = async (encodedForm) => {
  const response = await fetch("/forms.html", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: encodedForm,
  });

  if (!response.ok) {
    throw new Error("We could not submit your details right now. Please try again.");
  }
};

const renderMetaText = (meta) => {
  if (!meta || !/<[^>]+>/.test(meta)) {
    return meta;
  }

  return <span dangerouslySetInnerHTML={{ __html: meta }} />;
};

const RESOURCE_FORMS = {
  "email-updates": {
    formName: "risk-insights-updates",
    subject: "Risk Insights & Updates sign-up",
    title: "Risk Insights & Updates",
    description: "Share your details and we’ll keep you informed with practical risk insights and upcoming events.",
  },
  "professional-community": {
    formName: "best-practice-operational-risk-forum",
    subject: "Best Practice Operational Risk Forum interest",
    title: "Best Practice Operational Risk Forum",
    description: "Share a few details and we’ll be in touch about joining the practitioner community.",
  },
};

export const Resources = () => {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isInterestModalOpen, setIsInterestModalOpen] = useState(false);
  const [downloadResourceKey, setDownloadResourceKey] = useState("corporate-training-and-education-portfolio");
  const [interestResourceKey, setInterestResourceKey] = useState("email-updates");
  const [downloadFormData, setDownloadFormData] = useState({ name: "", organisation: "", email: "" });
  const [interestFormData, setInterestFormData] = useState({ name: "", organisation: "", email: "" });
  const [downloadErrors, setDownloadErrors] = useState({});
  const [interestErrors, setInterestErrors] = useState({});
  const [isDownloadSubmitting, setIsDownloadSubmitting] = useState(false);
  const [isInterestSubmitting, setIsInterestSubmitting] = useState(false);
  const [isDownloadSubmitted, setIsDownloadSubmitted] = useState(false);
  const [isInterestSubmitted, setIsInterestSubmitted] = useState(false);
  const currentDownload = DOWNLOAD_RESOURCES[downloadResourceKey];
  const currentInterest = RESOURCE_FORMS[interestResourceKey] || RESOURCE_FORMS["email-updates"];

  const validateDownload = (values) => {
    const next = {};
    if (!values.name.trim()) next.name = "Please share your name";
    if (!values.email.trim()) next.email = "An email address is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      next.email = "That email does not look right";
    }
    return next;
  };

  const validateInterest = (values) => {
    const next = {};
    if (!values.name.trim()) next.name = "Please share your name";
    if (!values.email.trim()) next.email = "An email address is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      next.email = "That email does not look right";
    }
    return next;
  };

  const resetDownloadModal = () => {
    setDownloadFormData({ name: "", organisation: "", email: "" });
    setDownloadErrors({});
    setIsDownloadSubmitting(false);
    setIsDownloadSubmitted(false);
  };

  const resetInterestModal = () => {
    setInterestFormData({ name: "", organisation: "", email: "" });
    setInterestErrors({});
    setIsInterestSubmitting(false);
    setIsInterestSubmitted(false);
  };

  const handleOpenDownloadModal = (event, resourceKey = "corporate-training-and-education-portfolio") => {
    event.preventDefault();
    setDownloadResourceKey(resourceKey);
    resetDownloadModal();
    setIsDownloadModalOpen(true);
  };

  const handleOpenInterestModal = (event, resourceKey = "email-updates") => {
    event.preventDefault();
    setInterestResourceKey(resourceKey);
    resetInterestModal();
    setIsInterestModalOpen(true);
  };

  const handleDownloadChange = (event) => {
    const { name, value } = event.target;
    setDownloadFormData((current) => ({ ...current, [name]: value }));
    if (downloadErrors[name]) {
      setDownloadErrors((current) => ({ ...current, [name]: undefined }));
    }
  };

  const handleInterestChange = (event) => {
    const { name, value } = event.target;
    setInterestFormData((current) => ({ ...current, [name]: value }));
    if (interestErrors[name]) {
      setInterestErrors((current) => ({ ...current, [name]: undefined }));
    }
  };

  const handleDownloadSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    formData.set("resource", downloadResourceKey);
    const encodedForm = new URLSearchParams(formData).toString();
    const nextErrors = validateDownload(downloadFormData);
    setDownloadErrors(nextErrors);

    if (Object.keys(nextErrors).length) return;

    setIsDownloadSubmitting(true);

    try {
      await submitNetlifyForm(encodedForm);

      const response = await fetch("/.netlify/functions/download-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encodedForm,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Unable to prepare download");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = currentDownload.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setIsDownloadSubmitted(true);
    } catch (error) {
      setDownloadErrors((current) => ({
        ...current,
        form: error.message || "We could not prepare the PDF right now. Please try again.",
      }));
    } finally {
      setIsDownloadSubmitting(false);
    }
  };

  const handleInterestSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const nextValues = {
      name: formData.get("name") || "",
      organisation: formData.get("organisation") || "",
      email: formData.get("email") || "",
    };
    const nextErrors = validateInterest(nextValues);
    setInterestErrors(nextErrors);

    if (Object.keys(nextErrors).length) return;

    setIsInterestSubmitting(true);

    try {
      formData.set("subject", currentInterest.subject);
      formData.set("resource", interestResourceKey);

      await submitNetlifyForm(new URLSearchParams(formData).toString());
      setIsInterestSubmitted(true);
    } catch (error) {
      setInterestErrors((current) => ({
        ...current,
        form: error.message || "We could not register your interest right now. Please try again.",
      }));
    } finally {
      setIsInterestSubmitting(false);
    }
  };

  return (
    <section
      id="resources"
      data-testid="resources-section"
      className="relative py-16 md:py-24 bg-[#F9F8F6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-20 items-end">
          <div className="lg:col-span-7">
            <p className="font-sans-body text-xs tracking-widest-2 uppercase text-[#3E7373] mb-6 flex items-center gap-3">
              <span className="inline-block w-10 h-px bg-[#3E7373]" />
              Resources
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-[64px] leading-[1.02] tracking-tight text-[#0D0D0D] font-medium">
              Insights, resources
              <br />
              <span className="italic text-[#3E7373]">and connections.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 font-sans-body text-[#4A4A4A] text-base md:text-lg leading-relaxed">
            Practical insights, tools and resources for risk professionals. Explore what is useful. Stay connected.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
          {RESOURCES.map((res, idx) => {
            const isInteractiveCard = !res.external && res.resourceKey;
            const CardComponent = res.external ? motion.a : motion.button;

            return (
              <CardComponent
                key={res.title}
                href={res.external ? res.url : undefined}
                target={res.external ? "_blank" : undefined}
                rel={res.external ? "noreferrer noopener" : undefined}
                type={isInteractiveCard ? "button" : undefined}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.8, delay: idx * 0.1, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`resource-${idx}`}
                onClick={(event) => {
                  if (res.external) return;

                  if (res.type === "Email updates" || res.type === "Professional community") {
                    handleOpenInterestModal(event, res.resourceKey);
                    return;
                  }

                  if (res.resourceKey) {
                    handleOpenDownloadModal(event, res.resourceKey);
                  }
                }}
                className="group bg-white p-8 md:p-10 flex flex-col hover:bg-[#F9F8F6] transition-colors duration-500 text-left"
              >
                <div className="flex items-center justify-between mb-10">
                  <span className="inline-flex items-center gap-2 font-sans-body text-[10px] tracking-widest-2 uppercase text-[#3E7373]">
                    <span className="block w-4 h-px bg-[#3E7373]" />
                    {res.type}
                  </span>
                  <div className="w-11 h-11 rounded-full bg-[#F9F8F6] border border-[#0D0D0D]/10 flex items-center justify-center text-[#3E7373] group-hover:bg-white transition-colors duration-500">
                    <res.icon size={18} strokeWidth={1.3} />
                  </div>
                </div>

                <h3 className="font-serif-display text-[22px] md:text-[24px] text-[#0D0D0D] leading-[1.2] mb-4">
                  {res.title}
                </h3>
                <p className="font-sans-body text-[13px] text-[#4A4A4A] mb-10">
                  {renderMetaText(res.meta)}
                </p>

                <span className="mt-auto inline-flex items-center gap-2 font-sans-body text-[11px] tracking-widest-2 uppercase text-[#0D0D0D] group-hover:text-[#3E7373] transition-colors duration-300">
                  {res.action}
                  <ExternalLink size={13} strokeWidth={1.4} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-500" />
                </span>
              </CardComponent>
            );
          })}
        </div>

        <p className="mt-10 text-center font-sans-body text-xs text-[#4A4A4A]/70 italic">
          More resources are being added. To request access to specific research or bespoke tools,{" "}
          <a href="#contact" className="text-[#3E7373] underline underline-offset-4 hover:text-[#0D0D0D]">get in touch</a>.
        </p>
      </div>

      <Dialog
        open={isDownloadModalOpen}
        onOpenChange={(open) => {
          setIsDownloadModalOpen(open);
          if (!open) resetDownloadModal();
        }}
      >
        <DialogContent className="sm:max-w-md bg-white border-[#E5E5E5]">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#0D0D0D]">
              {currentDownload.label}
            </DialogTitle>
            <DialogDescription className="font-sans-body text-sm text-[#4A4A4A]">
              {currentDownload.description}
            </DialogDescription>
          </DialogHeader>

          {isDownloadSubmitted ? (
            <div className="space-y-4 pt-2">
              <p className="font-sans-body text-sm text-[#3E7373]">
                Thanks! Your download should begin shortly.
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full border-[#0D0D0D]/20 text-[#0D0D0D] hover:bg-[#F9F8F6]"
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = `/.netlify/functions/download-pdf?resource=${encodeURIComponent(downloadResourceKey)}`;
                  link.download = currentDownload.fileName;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                Download PDF again
              </Button>
            </div>
          ) : (
            <form
              onSubmit={handleDownloadSubmit}
              method="POST"
              action="/forms.html"
              name="resource-download"
              data-netlify="true"
              className="space-y-5 pt-2"
            >
              <input type="hidden" name="form-name" value="resource-download" />
              {downloadErrors.form && (
                <p className="text-sm text-red-600">{downloadErrors.form}</p>
              )}
              <div>
                <label htmlFor="download-name" className="block font-sans-body text-[11px] tracking-widest-2 uppercase text-[#3E7373] mb-2">
                  Your name
                </label>
                <input
                  id="download-name"
                  name="name"
                  type="text"
                  value={downloadFormData.name}
                  onChange={handleDownloadChange}
                  className="w-full border border-[#0D0D0D]/20 bg-white px-4 py-3 font-sans-body text-sm text-[#0D0D0D] focus:border-[#3E7373] focus:outline-none"
                />
                {downloadErrors.name && <p className="mt-2 text-xs text-red-600">{downloadErrors.name}</p>}
              </div>

              <div>
                <label htmlFor="download-organisation" className="block font-sans-body text-[11px] tracking-widest-2 uppercase text-[#3E7373] mb-2">
                  Organisation
                </label>
                <input
                  id="download-organisation"
                  name="organisation"
                  type="text"
                  value={downloadFormData.organisation}
                  onChange={handleDownloadChange}
                  className="w-full border border-[#0D0D0D]/20 bg-white px-4 py-3 font-sans-body text-sm text-[#0D0D0D] focus:border-[#3E7373] focus:outline-none"
                />
                {downloadErrors.organisation && <p className="mt-2 text-xs text-red-600">{downloadErrors.organisation}</p>}
              </div>

              <div>
                <label htmlFor="download-email" className="block font-sans-body text-[11px] tracking-widest-2 uppercase text-[#3E7373] mb-2">
                  Email address
                </label>
                <input
                  id="download-email"
                  name="email"
                  type="email"
                  value={downloadFormData.email}
                  onChange={handleDownloadChange}
                  className="w-full border border-[#0D0D0D]/20 bg-white px-4 py-3 font-sans-body text-sm text-[#0D0D0D] focus:border-[#3E7373] focus:outline-none"
                />
                {downloadErrors.email && <p className="mt-2 text-xs text-red-600">{downloadErrors.email}</p>}
              </div>

              <Button
                type="submit"
                className="w-full bg-[#0D0D0D] text-white hover:bg-[#3E7373]"
                disabled={isDownloadSubmitting}
              >
                {isDownloadSubmitting ? "Preparing download..." : "Get the PDF"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={isInterestModalOpen}
        onOpenChange={(open) => {
          setIsInterestModalOpen(open);
          if (!open) resetInterestModal();
        }}
      >
        <DialogContent className="sm:max-w-md bg-white border-[#E5E5E5]">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#0D0D0D]">
              {currentInterest.title}
            </DialogTitle>
            <DialogDescription className="font-sans-body text-sm text-[#4A4A4A]">
              {currentInterest.description}
            </DialogDescription>
          </DialogHeader>

          {isInterestSubmitted ? (
            <div className="space-y-3 pt-2">
              <p className="font-sans-body text-sm text-[#3E7373]">
                Thanks for your interest. I’ll be in touch soon.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleInterestSubmit}
              method="POST"
              action="/forms.html"
              name={currentInterest.formName}
              data-netlify="true"
              className="space-y-5 pt-2"
            >
              <input type="hidden" name="form-name" value={currentInterest.formName} />
              <input type="hidden" name="subject" value={currentInterest.subject} />
              <input type="hidden" name="resource" value={interestResourceKey} />
              {interestErrors.form && (
                <p className="text-sm text-red-600">{interestErrors.form}</p>
              )}
              <div>
                <label htmlFor="interest-name" className="block font-sans-body text-[11px] tracking-widest-2 uppercase text-[#3E7373] mb-2">
                  Your name
                </label>
                <input
                  id="interest-name"
                  name="name"
                  type="text"
                  value={interestFormData.name}
                  onChange={handleInterestChange}
                  className="w-full border border-[#0D0D0D]/20 bg-white px-4 py-3 font-sans-body text-sm text-[#0D0D0D] focus:border-[#3E7373] focus:outline-none"
                />
                {interestErrors.name && <p className="mt-2 text-xs text-red-600">{interestErrors.name}</p>}
              </div>

              <div>
                <label htmlFor="interest-organisation" className="block font-sans-body text-[11px] tracking-widest-2 uppercase text-[#3E7373] mb-2">
                  Organisation
                </label>
                <input
                  id="interest-organisation"
                  name="organisation"
                  type="text"
                  value={interestFormData.organisation}
                  onChange={handleInterestChange}
                  className="w-full border border-[#0D0D0D]/20 bg-white px-4 py-3 font-sans-body text-sm text-[#0D0D0D] focus:border-[#3E7373] focus:outline-none"
                />
                {interestErrors.organisation && <p className="mt-2 text-xs text-red-600">{interestErrors.organisation}</p>}
              </div>

              <div>
                <label htmlFor="interest-email" className="block font-sans-body text-[11px] tracking-widest-2 uppercase text-[#3E7373] mb-2">
                  Email address
                </label>
                <input
                  id="interest-email"
                  name="email"
                  type="email"
                  value={interestFormData.email}
                  onChange={handleInterestChange}
                  className="w-full border border-[#0D0D0D]/20 bg-white px-4 py-3 font-sans-body text-sm text-[#0D0D0D] focus:border-[#3E7373] focus:outline-none"
                />
                {interestErrors.email && <p className="mt-2 text-xs text-red-600">{interestErrors.email}</p>}
              </div>

              <Button
                type="submit"
                className="w-full bg-[#0D0D0D] text-white hover:bg-[#3E7373]"
                disabled={isInterestSubmitting}
              >
                {isInterestSubmitting
                  ? "Submitting..."
                  : interestResourceKey === "email-updates"
                    ? "Sign up"
                    : "Register interest"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Resources;
